import {
  CreateCertificateMonitorDTO,
  CreateHttpsMonitorDTO,
  CreateLatencyMonitorDTO,
  CreateMonitorDTO,
  UpdateMonitorDTO,
} from '@meshwatch/types';
import { Dictionary } from 'lodash';
import * as Yup from 'yup';

import { typeErrorHandler, validate } from '../../shared/validation';
import { userIdSchema, uuidIdSchema } from '../../shared/validation/schemas';
import { ValidationResponse } from '../../shared/validation/types';
import { EXECUTE_MONITOR_SCHEMA_OBJECT } from '../executors/validation';

const monitorTypeErrorHandler = (params: Partial<Yup.TestMessageParams>): string => {
  return typeErrorHandler('Monitor', params);
};

/* Monitors */
const CREATE_MONITOR_BASE_SCHEMA_SHAPE = {
  ...EXECUTE_MONITOR_SCHEMA_OBJECT,
  userId: userIdSchema,
  isBookmarked: Yup.boolean().required('isBookmarked is required'),
  scheduler: Yup.string().required(
    'Scheduler is required, so we know how often to monitor your endpoint'
  ),
  name: Yup.string().required('Name is required, so you can find your monitors easily'),
};

const UPDATE_MONITOR_BASE_SCHEMA_SHAPE = {
  id: uuidIdSchema,
};

export const CREATE_MONITOR_SCHEMA = Yup.object()
  .shape<CreateCertificateMonitorDTO | CreateHttpsMonitorDTO>(CREATE_MONITOR_BASE_SCHEMA_SHAPE)
  .typeError(monitorTypeErrorHandler);

export const UPDATE_MONITOR_SCHEMA = Yup.object()
  .shape<UpdateMonitorDTO>({
    ...CREATE_MONITOR_BASE_SCHEMA_SHAPE,
    ...UPDATE_MONITOR_BASE_SCHEMA_SHAPE,
  })
  .typeError(monitorTypeErrorHandler);

/* Latency check */

const CREATE_LATENCY_CHECK_SCHEMA_SHAPE = {
  ...CREATE_MONITOR_BASE_SCHEMA_SHAPE,
  body: Yup.string(),
  headers: Yup.object() as Yup.ObjectSchema<Dictionary<string>>,
  apdex: Yup.number().required(
    'Apdex value is required so we can better measure performance of your endpoint'
  ),
  regions: Yup.array()
    .of(Yup.string())
    .required('Regions are required so we know from where to monitor your endpoint')
    .test(
      'isNonEmpty',
      'At least one region should be selected but got: `${value}`',
      (value: string[] | undefined) => (value || []).length > 0
    ) as Yup.ArraySchema<string>,
};

export const CREATE_LATENCY_MONITOR_SCHEMA = Yup.object()
  .shape<CreateLatencyMonitorDTO>(CREATE_LATENCY_CHECK_SCHEMA_SHAPE)
  .typeError(monitorTypeErrorHandler);

export const UPDATE_LATENCY_MONITOR_SCHEMA = Yup.object()
  .shape<UpdateMonitorDTO>({
    ...CREATE_LATENCY_CHECK_SCHEMA_SHAPE,
    ...UPDATE_MONITOR_BASE_SCHEMA_SHAPE,
  })
  .typeError(monitorTypeErrorHandler);

export function isLatencyCheckMonitor(
  payload: CreateMonitorDTO
): payload is CreateLatencyMonitorDTO {
  return payload.type === 'latency-check';
}

export function validateCreateMonitorPayload(
  payload: CreateMonitorDTO
): Promise<ValidationResponse<CreateMonitorDTO>> {
  if (isLatencyCheckMonitor(payload)) {
    return validate<CreateMonitorDTO>(CREATE_LATENCY_MONITOR_SCHEMA, payload);
  }
  return validate<CreateMonitorDTO>(CREATE_MONITOR_SCHEMA, payload);
}

export function validateUpdateMonitorPayload(
  payload: UpdateMonitorDTO
): Promise<ValidationResponse<UpdateMonitorDTO>> {
  if (isLatencyCheckMonitor(payload)) {
    return validate<UpdateMonitorDTO>(UPDATE_LATENCY_MONITOR_SCHEMA, payload);
  }
  return validate<UpdateMonitorDTO>(UPDATE_MONITOR_SCHEMA, payload);
}
